// Import package components
import { useContext, useCallback, useEffect, useMemo, useState } from "react";
import { Image, InputContainer, Flex } from "uikit-react";

import { useForm } from "react-hook-form";

import { CopyToClipboard } from "react-copy-to-clipboard";

import { AppContexts } from "../../../providers";
import AddTextLine from "../../common/AddTextLine";

const max_welcome_msg_length = 144;
const max_auto_response_msg_length = 144;
const max_out_of_office_msg_length = 144;

const validateColor = (color) => {
  var s = new Option().style;
  s.color = color;
  return s.color !== "" ? color : "rgba(0, 0, 0, 0)";
};

const ChatTag = () => {
  const { set, settings, loading, active } = useContext(
    AppContexts.SettingsContext,
  );
  const { handleSubmit } = useForm();

  const [dirty, setDirty] = useState(false);
  const [chat, setChat] = useState({});
  const [copied, setCopied] = useState(false);
  const [domains, setDomains] = useState("");

  useEffect(() => {
    document.title = "Settings | AI Chatbot & Text Webchat - RueBaRue";
    if (!!settings.chat) {
      setChat(settings.chat);
      setDomains((settings.chat?.domains || []).join(", "));
    }
  }, [settings]);

  const handleRegister = (data) => {
    set("chat", chat).then((res) => {
      setDirty(false);
    });
  };

  const updateChat = (field, e) => {
    setChat({ ...chat, [field]: e.target.value });

    setDirty(true);
  };

  const updateDomains = (e) => {
    setDomains(e.target.value);
    setChat({
      ...chat,
      domains: e.target.value
        .split(",")
        .map((domain) => domain.trim())
        .map((domain) => (/^http/.test(domain) ? domain : `https://${domain}`))
        .filter((domain) => !!domain),
    });
    setDirty(true);
  };

  const updateToggle = (field, e) => {
    setChat({ ...chat, [field]: !chat[field] });
    setDirty(true);
  };

  const embedCode = `<script id='rbr-chat' src='https://chatbot.ruebarue.com/prod/api/chat/${settings.account_id}/load.js'></script>`;
  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <li
      className={`setting-tab-content ${active === "chat" ? "uk-active" : ""}`}
    >
      <h2>AI Chatbot & Text Webchat</h2>
      <div className="uk-card hms-left-tabs-content-card mx-w-665">
        <AddTextLine feature="AI Chatbot & Text Webchat" />
        <form
          className="uk-grid-small"
          data-uk-grid
          onSubmit={handleSubmit(handleRegister)}
        >
          <div className="uk-width-1-1">
            <div className="uk-flex uk-flex-middle">
              <div className="autosend-checkbox">
                <label htmlFor="useWebChat" className="setting-switch">
                  <input
                    type="checkbox"
                    id="useWebChat"
                    checked={chat.web_active}
                    onInput={() => {
                      updateToggle("web_active");
                    }}
                  />
                  <span className="setting-slider setting-round"></span>
                </label>
              </div>
              <div className="uk-form-label uk-margin-small-left">
                Enable Text Webchat
              </div>
            </div>
          </div>

          <div className="uk-width-1-1 uk-margin-top">
            <div className="uk-flex uk-flex-middle">
              <div className="autosend-checkbox">
                <label htmlFor="aiEnabled" className="setting-switch">
                  <input
                    type="checkbox"
                    id="aiEnabled"
                    checked={!!chat.ai_enabled}
                    onChange={() => {
                      setChat({ ...chat, ai_enabled: !chat.ai_enabled });
                      setDirty(true);
                    }}
                  />
                  <span className="setting-slider setting-round"></span>
                </label>
              </div>
              <div className="uk-form-label uk-margin-small-left">
                Enable AI Chatbot
              </div>
            </div>
          </div>
          <div className="uk-width-1-1 uk-margin-top">
            <div className="uk-form-label">Icon Position</div>
            <div className="uk-form-controls uk-flex uk-flex-middle">
              <label>
                <input
                  className="uk-radio"
                  type="radio"
                  name="chatPosition"
                  value="right"
                  checked={chat.position === "right"}
                  onChange={() => {
                    setChat({ ...chat, position: "right" });
                    setDirty(true);
                  }}
                />{" "}
                Right
              </label>
              <label className="uk-margin-left">
                <input
                  className="uk-radio"
                  type="radio"
                  name="chatPosition"
                  value="left"
                  checked={chat.position === "left"}
                  onChange={() => {
                    setChat({ ...chat, position: "left" });
                    setDirty(true);
                  }}
                />{" "}
                Left
              </label>
            </div>
          </div>
          <div className="uk-width-1-1">
            <div className="uk-flex uk-flex-middle">
              <div className="uk-width-1-3">
                <label className="uk-form-label">Color</label>
                <div className="uk-form-controls setting-color-box">
                  <div className="uk-flex uk-flex-middle">
                    <div
                      className="rbr-color-preview"
                      style={{
                        backgroundColor: validateColor(chat.color || "#000000"),
                      }}
                    >
                      <input
                        type="color"
                        name="color"
                        id="color"
                        value={chat.color || "#000000"}
                        onChange={(e) => {
                          setChat({ ...chat, color: e.target.value });
                          setDirty(true);
                        }}
                      />
                    </div>
                    <input
                      className="setting-color-value"
                      id="tab-BG-color"
                      value={chat.color || ""}
                      onChange={(e) => {
                        setChat({ ...chat, color: e.target.value });
                        setDirty(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="uk-width-1-1">
            <div className="uk-form-label">Embed Script</div>
            <div className="uk-form-controls">
              <textarea
                className="uk-textarea"
                rows="3"
                disabled="true"
                value={embedCode}
              ></textarea>
            </div>
            <p className=" secondary">
              Place the code right before the end of the &lt;body&gt; tag on any
              page that you want to enable Webchat on.
            </p>
            <div className="uk-align-right uk-text-small">
              <CopyToClipboard text={embedCode} onCopy={handleCopy}>
                <div
                  style={{ cursor: "pointer" }}
                  uk-tooltip="Copy to Clipboard"
                  className="uk-flex uk-flex-middle"
                >
                  <span className="uk-text-small uk-margin-small-right">
                    {copied ? "Copied" : "Copy"}
                  </span>
                  {copied ? (
                    <Image
                      src="/images/check-icon.svg"
                      style={{ width: "18px", height: "18px" }}
                      alt="Copied"
                    />
                  ) : (
                    <Image
                      src="/images/copy-macro-icon.svg"
                      style={{ width: "18px", height: "18px" }}
                      alt="Copy"
                    />
                  )}
                </div>
              </CopyToClipboard>
            </div>
          </div>

          <div className="uk-width-1-1">
            <div className="uk-form-label">
              Allowed Domains{" "}
              <img
                className="info-icon"
                src="/images/info-icon.svg"
                alt="info"
                uk-tooltip="Comma-separated list of domains where the chatbot is allowed to run"
              />
            </div>
            <div className="uk-form-controls">
              <input
                className="uk-input"
                type="text"
                placeholder="example.com, another-domain.com"
                value={domains}
                onInput={(e) => {
                  updateDomains(e);
                }}
              />
            </div>
            <p className="secondary">Enter domains separated by commas</p>
          </div>

          <div className="uk-width-1-1">
            <div className="uk-form-label">Chatbot Intro Text</div>
            <div className="uk-form-controls">
              <textarea
                className="uk-textarea"
                rows="3"
                maxLength={max_welcome_msg_length}
                value={chat.chatbot_intro_msg || ""}
                onInput={(e) => {
                  updateChat("chatbot_intro_msg", e);
                }}
              ></textarea>
            </div>
            <p className="secondary">
              Characters Remaining:{" "}
              {max_welcome_msg_length - (chat.chatbot_intro_msg?.length || 0)}
            </p>
          </div>

          <div className="uk-width-1-1">
            <div className="uk-form-label">Chatbot No Response Text</div>
            <div className="uk-form-controls">
              <textarea
                className="uk-textarea"
                rows="3"
                maxLength={max_welcome_msg_length}
                value={chat.chatbot_auto_response_msg || ""}
                onInput={(e) => {
                  updateChat("chatbot_auto_response_msg", e);
                }}
              ></textarea>
            </div>
            <p className="secondary">
              Characters Remaining:{" "}
              {max_welcome_msg_length -
                (chat.chatbot_auto_response_msg?.length || 0)}
            </p>
          </div>

          <div className="uk-width-1-1">
            <div className="uk-form-label">Text Us Intro Text</div>
            <div className="uk-form-controls">
              <textarea
                className="uk-textarea"
                rows="3"
                maxLength={max_welcome_msg_length}
                value={chat.welcome_msg || ""}
                onInput={(e) => {
                  updateChat("welcome_msg", e);
                }}
              ></textarea>
            </div>
            <p className="secondary">
              Characters Remaining:{" "}
              {max_welcome_msg_length - (chat.welcome_msg?.length || 0)}
            </p>
          </div>

          <div className="uk-width-1-1">
            <div className="uk-form-label">Text Us Auto Response</div>
            <div className="uk-form-controls">
              <textarea
                className="uk-textarea"
                rows="3"
                maxLength={max_auto_response_msg_length}
                value={chat.auto_response_msg}
                onInput={(e) => {
                  updateChat("auto_response_msg", e);
                }}
              ></textarea>
            </div>
            <p className="secondary">
              Characters Remaining:{" "}
              {max_auto_response_msg_length - chat.auto_response_msg?.length}
            </p>
          </div>

          <div className="uk-width-1-1">
            <div className="uk-form-label">Text Us Out-of-Office Response</div>
            <div className="uk-form-controls">
              <textarea
                className="uk-textarea"
                rows="3"
                maxLength={max_out_of_office_msg_length}
                value={chat.out_of_office_msg}
                onInput={(e) => {
                  updateChat("out_of_office_msg", e);
                }}
              ></textarea>
            </div>
            <p className="secondary">
              Characters Remaining:{" "}
              {max_out_of_office_msg_length - chat.out_of_office_msg?.length}
            </p>
          </div>

          <div className="uk-width-1-1 uk-margin-top">
            <button
              className="uk-button hms-btn hms-red-btn"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <div data-uk-spinner="ratio: 0.5;"></div>
              ) : dirty ? (
                "Save"
              ) : (
                "Saved"
              )}
            </button>
          </div>
        </form>
      </div>
    </li>
  );
};

export default ChatTag;
